import { useEffect, useRef } from 'react';

import type { ProfileCookieSettings } from '@/api/core-api/types.gen';
import type { User } from '@/utils/session/fetch-user';
import type { Profile } from '@/utils/session/get-profile';

import { useUpdateProfile } from '@/hooks/api/use-update-profile';

import {
  choicesEqual,
  choicesFromProfileCookieSettings,
  profileCookieSettingsFromChoices,
  profileConsentVersion,
} from './consent-profile';
import { useConsent } from './use-consent';

/**
 * Write-through from the recorded consent to the durable profile copy.
 *
 * Only ever runs cookie → profile. The other direction (profile → in-memory
 * consent, for a signed-in user on a browser with no cookie) is handled
 * once during resolution in `ConsentProvider`. That read does not write a
 * cookie: only the banner and the settings panel record a decision.
 *
 * Nothing is written for a visitor who has not decided: `stored === null`
 * means "no choice yet", not "opted out". Nothing is written either for a
 * record that is not syncable — a merge of pre-unification cookies — or for
 * an implied default, which is why what persists here is the stored answers
 * rather than the effective choices.
 */
export function ConsentProfileSync({
  user,
  profile,
}: {
  user: User | null;
  profile: Profile | null;
}) {
  const { isResolved, stored } = useConsent();
  const updateProfile = useUpdateProfile();

  // The mutation's identity changes every render; hold it in a ref so the
  // effect below is driven purely by consent/profile state.
  const updateProfileRef = useRef(updateProfile);
  updateProfileRef.current = updateProfile;

  // Signature of the last write we issued, so an in-flight PATCH (or a server
  // that does not echo the field back) can't make us fire repeatedly.
  const lastWrittenRef = useRef<string | null>(null);
  // The signature currently in flight, and the one that arrived while it was.
  // Without this a fast change lands out of order — the user denies, the earlier
  // grant's PATCH settles last, and the profile keeps the grant. The cookie is
  // still right on this device, so nothing looks wrong until they sign in
  // somewhere else and the stale profile re-hydrates it.
  const inFlightRef = useRef<string | null>(null);
  const pendingRef = useRef<{
    signature: string;
    target: ProfileCookieSettings;
  } | null>(null);

  useEffect(() => {
    if (!isResolved || !stored?.isSyncable || !user || !profile) return;

    const target = profileCookieSettingsFromChoices(stored.choices, stored.v);
    const signature = `${profile.id}:${JSON.stringify(target)}`;
    if (lastWrittenRef.current === signature) return;

    const current = choicesFromProfileCookieSettings(profile.cookieSettings);
    const isUpToDate =
      current !== null &&
      choicesEqual(current, stored.choices) &&
      profileConsentVersion(profile.cookieSettings) === stored.v;
    if (isUpToDate) return;

    if (inFlightRef.current !== null) {
      // Queue only the newest target; it supersedes anything else waiting.
      pendingRef.current = { signature, target };
      return;
    }

    issueWrite(signature, target);

    function issueWrite(
      writeSignature: string,
      writeTarget: ProfileCookieSettings,
    ) {
      if (!user || !profile) return;

      lastWrittenRef.current = writeSignature;
      inFlightRef.current = writeSignature;

      updateProfileRef.current.mutate(
        {
          profileId: profile.id,
          userId: user.id,
          updateProfileDto: { cookieSettings: writeTarget },
        },
        {
          onError: () => {
            // Allow a retry on the next resolution pass.
            lastWrittenRef.current = null;
            console.error('Failed to persist cookie consent to profile');
          },
          onSettled: () => {
            inFlightRef.current = null;
            const queued = pendingRef.current;
            pendingRef.current = null;
            // Re-issue the newest target so the last decision is what persists,
            // whatever order the requests happened to settle in.
            if (queued && queued.signature !== lastWrittenRef.current) {
              issueWrite(queued.signature, queued.target);
            }
          },
        },
      );
    }
  }, [isResolved, stored, user, profile]);

  return null;
}
